import { ReactNode, useCallback, useMemo, useState } from 'react'

import { Command } from '@cowprotocol/types'
import { Badge, BadgeTypes, HoverTooltip, Modal } from '@cowprotocol/ui'
import type { TradeType } from '@cowprotocol/widget-lib'

import { Trans, useLingui } from '@lingui/react/macro'
import iconCaretSrc from 'assets/icon/caret.svg'
import { useInjectedWidgetParams } from 'entities/injectedWidget'
import SVG from 'react-inlinesvg'
import { useLocation } from 'react-router'

import { IMenuItem, Routes, RoutesValues } from 'common/constants/routes'
import { useMenuItems } from 'common/hooks/useMenuItems'
import { TradeUrlParams, addChainIdToRoute, parameterizeTradeRoute } from 'common/modules/tradeNavigation'
import { isTwapSupportedChain } from 'common/utils/isTwapSupportedChain'

import * as styledEl from './styled'

import { useGetTradeUrlParams } from '../../hooks/useGetTradeUrlParams'
import { useTradeRouteContext } from '../../hooks/useTradeRouteContext'
import { useGetTradeStateByRoute } from '../../hooks/useTradeState'
import { getDefaultTradeRawState } from '../../types'

interface MenuItemConfig {
  route: RoutesValues
  label: string
  badge?: string
  badgeImage?: string
  badgeType?: (typeof BadgeTypes)[keyof typeof BadgeTypes]
}

const TRADE_TYPE_TO_ROUTE: Record<TradeType, string> = {
  swap: Routes.SWAP,
  limit: Routes.LIMIT_ORDERS,
  advanced: Routes.ADVANCED_ORDERS,
  yield: Routes.YIELD,
}

interface TradeWidgetLinksProps {
  isDropdown?: boolean
}

export function TradeWidgetLinks({ isDropdown = false }: TradeWidgetLinksProps): ReactNode {
  const tradeContext = useTradeRouteContext()
  const location = useLocation()
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const { enabledTradeTypes } = useInjectedWidgetParams()
  const menuItems = useMenuItems()
  const getTradeStateByType = useGetTradeStateByRoute()
  const getTradeUrlParams = useGetTradeUrlParams()

  const handleMenuItemClick = useCallback((_item?: MenuItemConfig): void => {
    setDropdownVisible(false)
  }, [])

  const enabledItems = useMemo(() => {
    return menuItems.filter((item) => {
      if (item.route === Routes.RWA) return false
      if (!enabledTradeTypes?.length) return true

      return enabledTradeTypes.some((type: TradeType) => TRADE_TYPE_TO_ROUTE[type] === item.route)
    })
  }, [menuItems, enabledTradeTypes])

  const menuItemsElements = useMemo(() => {
    return enabledItems.map((item) => {
      const isItemYield = item.route === Routes.YIELD
      const chainId = tradeContext.chainId
      const isDisabled = item.route === Routes.ADVANCED_ORDERS && !isTwapSupportedChain(Number(chainId))

      const isCurrentPathYield = location.pathname.startsWith(addChainIdToRoute(Routes.YIELD, chainId))
      const itemTradeState = getTradeStateByType(item.route)
      const defaultState = chainId ? getDefaultTradeRawState(+chainId) : null

      const tradeUrlParams = isCurrentPathYield
        ? ({
            chainId,
            inputCurrencyId: itemTradeState.inputCurrencyId || defaultState?.inputCurrencyId || null,
            outputCurrencyId: itemTradeState.outputCurrencyId,
          } as TradeUrlParams)
        : getTradeUrlParams(item)

      const routePath =
        isItemYield && !isCurrentPathYield
          ? addChainIdToRoute(item.route, chainId)
          : parameterizeTradeRoute(tradeUrlParams, item.route, !isCurrentPathYield)

      const routeBasePath = addChainIdToRoute(item.route, chainId)
      const hooksBasePath = addChainIdToRoute(Routes.HOOKS, chainId)
      const isActive =
        location.pathname.startsWith(routeBasePath) &&
        (item.route === Routes.HOOKS || !location.pathname.startsWith(hooksBasePath))

      return (
        <MenuItem
          key={item.label}
          routePath={routePath}
          item={item}
          isActive={isActive}
          isDisabled={isDisabled}
          onClick={() => handleMenuItemClick(item)}
          isDropdownVisible={isDropdown && isDropdownVisible}
        />
      )
    })
  }, [
    isDropdown,
    isDropdownVisible,
    enabledItems,
    tradeContext,
    location.pathname,
    handleMenuItemClick,
    getTradeStateByType,
    getTradeUrlParams,
  ])

  const singleMenuItem = menuItemsElements.length === 1

  const selectedMenuItem = menuItemsElements.find((item) => item.props.isActive) || menuItemsElements[0]

  const { t } = useLingui()

  const rwaMenuItem = <RwaMenuItem menuItems={menuItems} isDropdownVisible={isDropdown && isDropdownVisible} />

  return isDropdown ? (
    <>
      <styledEl.MenuItem
        id="trading-mode-dropdown-button"
        onClick={() => !singleMenuItem && setDropdownVisible(!isDropdownVisible)}
        isDropdownVisible={isDropdownVisible}
      >
        <styledEl.DropdownButton>
          {selectedMenuItem.props.item.label}
          {!singleMenuItem ? <SVG src={iconCaretSrc} title={t`select`} /> : null}
        </styledEl.DropdownButton>
      </styledEl.MenuItem>

      {isDropdownVisible && (
        <styledEl.SelectMenu>
          <Modal.Header onBack={handleMenuItemClick}>
            <Trans>Trading mode</Trans>
          </Modal.Header>
          <styledEl.TradeWidgetContent>
            {menuItemsElements}
            {rwaMenuItem}
          </styledEl.TradeWidgetContent>
        </styledEl.SelectMenu>
      )}
    </>
  ) : (
    <styledEl.Wrapper>
      {menuItemsElements}
      {rwaMenuItem}
    </styledEl.Wrapper>
  )
}

const MenuItem = ({
  routePath,
  item,
  isActive,
  isDisabled,
  onClick,
  isDropdownVisible,
}: {
  isActive: boolean
  isDisabled?: boolean
  isDropdownVisible: boolean
  item: MenuItemConfig
  onClick: Command
  routePath: string
  // TODO: Add proper return type annotation
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}) => {
  const { t } = useLingui()

  const content = (
    <>
      {item.label}
      {(!isActive && item.badgeImage) || item.badge ? (
        <Badge {...(item.badgeType && { type: item.badgeType })}>
          {item.badgeImage ? <SVG src={item.badgeImage} /> : item.badge}
        </Badge>
      ) : null}
    </>
  )

  if (isDisabled) {
    const reason = t`TWAP is not available on Solana yet`

    return (
      <styledEl.MenuItem isDisabled isDropdownVisible={isDropdownVisible}>
        <styledEl.DisabledLink aria-disabled="true" aria-label={`${item.label}. ${reason}`}>
          <HoverTooltip wrapInContainer placement="top" content={reason}>
            <styledEl.DisabledLabel>{content}</styledEl.DisabledLabel>
          </HoverTooltip>
        </styledEl.DisabledLink>
      </styledEl.MenuItem>
    )
  }

  return (
    <styledEl.MenuItem isActive={isActive} onClick={onClick} isDropdownVisible={isDropdownVisible}>
      <styledEl.Link to={routePath}>{content}</styledEl.Link>
    </styledEl.MenuItem>
  )
}

function RwaMenuItem({
  menuItems,
  isDropdownVisible,
}: {
  menuItems: IMenuItem[]
  isDropdownVisible: boolean
}): ReactNode {
  const item = menuItems.find(({ route }) => route === Routes.RWA)

  if (!item) return null

  return (
    <styledEl.MenuItem isDropdownVisible={isDropdownVisible}>
      <styledEl.PageLink href={item.route}>
        {item.label}
        {item.badge && <Badge {...(item.badgeType && { type: item.badgeType })}>{item.badge}</Badge>}
      </styledEl.PageLink>
    </styledEl.MenuItem>
  )
}
